import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scansApi } from '../services/api';
import './ScanLoading.css';

// Minimum time to show loading screen (in seconds) - makes scan feel thorough
const MINIMUM_SCAN_TIME = 20;

export default function ScanLoading() {
    const { scanId } = useParams<{ scanId: string }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState('Initializing deep scan...');
    const [progress, setProgress] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [scanComplete, setScanComplete] = useState(false);
    const scanCompleteRef = useRef(false);

    useEffect(() => {
        if (!scanId) return;

        // Timer for elapsed time
        const timer = setInterval(() => {
            setElapsedTime((prev) => prev + 1);
        }, 1000);

        // Slower, smoother progress animation - reaches ~85% at MINIMUM_SCAN_TIME
        const progressTimer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 85) return prev;
                // Slower increment for more premium feel
                const increment = (85 - prev) * 0.03 + Math.random() * 0.5;
                return Math.min(prev + increment, 85);
            });
        }, 300);

        // More detailed status messages spread across the full scan time
        const statusMessages = [
            { time: 1, msg: 'Establishing secure connection...' },
            { time: 3, msg: 'Loading target page...' },
            { time: 5, msg: 'Waiting for page to fully render...' },
            { time: 7, msg: 'Capturing full-page screenshot...' },
            { time: 9, msg: 'Capturing above-the-fold view...' },
            { time: 11, msg: 'Analyzing network waterfall...' },
            { time: 13, msg: 'Inspecting JavaScript execution...' },
            { time: 15, msg: 'Checking for console errors...' },
            { time: 17, msg: 'Evaluating SEO elements...' },
            { time: 19, msg: 'Analyzing accessibility compliance...' },
            { time: 21, msg: 'Checking for broken resources...' },
            { time: 23, msg: 'Detecting layout issues...' },
            { time: 25, msg: 'Compiling diagnostic report...' },
            { time: 27, msg: 'Calculating confidence scores...' },
            { time: 29, msg: 'Finalizing analysis...' },
        ];

        const statusTimer = setInterval(() => {
            setElapsedTime((current) => {
                const nextStatus = statusMessages.find((s) => s.time === current);
                if (nextStatus) {
                    setStatus(nextStatus.msg);
                }
                return current;
            });
        }, 1000);

        // Poll for scan completion
        const pollScan = async () => {
            try {
                const data = await scansApi.get(parseInt(scanId));
                if (data.status === 'completed' && !scanCompleteRef.current) {
                    scanCompleteRef.current = true;
                    setScanComplete(true);
                } else if (data.status === 'failed') {
                    clearInterval(timer);
                    clearInterval(progressTimer);
                    clearInterval(statusTimer);
                    clearInterval(pollInterval);
                    setStatus('Scan encountered an issue. Loading results...');
                    setProgress(100);
                    setTimeout(() => {
                        navigate(`/report/${scanId}`, { replace: true });
                    }, 2000);
                }
            } catch (err) {
                console.error('Error polling scan:', err);
            }
        };

        const pollInterval = setInterval(pollScan, 2000);
        pollScan(); // Initial poll

        return () => {
            clearInterval(timer);
            clearInterval(progressTimer);
            clearInterval(statusTimer);
            clearInterval(pollInterval);
        };
    }, [scanId, navigate]);

    // Separate effect to handle navigation after minimum time
    useEffect(() => {
        if (scanComplete && elapsedTime >= MINIMUM_SCAN_TIME) {
            // Animate progress to 100% then navigate
            setProgress(100);
            setStatus('Analysis complete! Preparing your report...');

            setTimeout(() => {
                navigate(`/report/${scanId}`, { replace: true });
            }, 1500);
        }
    }, [scanComplete, elapsedTime, scanId, navigate]);

    // Load Adsterra ad
    useEffect(() => {
        const adContainer = document.getElementById('adsterra-container');
        if (adContainer && adContainer.children.length === 0) {
            // Adsterra native banner script
            const script = document.createElement('script');
            script.async = true;
            script.setAttribute('data-cfasync', 'false');
            script.src = '//pl25338498.profitablegatecpm.com/c67e9a0c95a4d3c86f7c14b3a0e1f2d8/invoke.js';

            const adDiv = document.createElement('div');
            adDiv.id = 'container-c67e9a0c95a4d3c86f7c14b3a0e1f2d8';

            adContainer.appendChild(adDiv);
            adContainer.appendChild(script);
        }
    }, []);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    };

    return (
        <div className="scan-loading">
            <div className="loading-container">
                {/* Header */}
                <div className="loading-header">
                    <img src="/assets/logo.png" alt="autopsyURL" className="loading-logo" />
                    <h1>Running Full Analysis</h1>
                    <p className="loading-status">{status}</p>
                </div>

                {/* Progress Bar */}
                <div className="progress-container">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                    </div>
                    <div className="progress-info">
                        <span className="progress-percent">{Math.round(progress)}%</span>
                        <span className="elapsed-time">{formatTime(elapsedTime)}</span>
                    </div>
                </div>

                {/* What we're checking - more items, slower reveal */}
                <div className="checking-list">
                    <h3>Deep Analysis In Progress</h3>
                    <ul>
                        <li className={elapsedTime >= 2 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 8 ? '✓' : '○'}</span>
                            Visual screenshots & viewport capture
                        </li>
                        <li className={elapsedTime >= 5 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 12 ? '✓' : '○'}</span>
                            Network waterfall & request analysis
                        </li>
                        <li className={elapsedTime >= 8 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 16 ? '✓' : '○'}</span>
                            JavaScript errors & console inspection
                        </li>
                        <li className={elapsedTime >= 11 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 18 ? '✓' : '○'}</span>
                            SEO metadata & accessibility audit
                        </li>
                        <li className={elapsedTime >= 14 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 20 ? '✓' : '○'}</span>
                            Performance metrics & load timing
                        </li>
                        <li className={elapsedTime >= 17 ? 'active' : ''}>
                            <span className="check-icon">{elapsedTime >= 22 ? '✓' : '○'}</span>
                            Security headers & mixed content
                        </li>
                    </ul>
                </div>

                {/* Ad Placement */}
                <div className="ad-section">
                    <p className="ad-label">Sponsored</p>
                    <div id="adsterra-container" className="ad-container">
                        {/* Adsterra ad will be injected here */}
                        <div className="ad-placeholder">
                            <span>Advertisement</span>
                        </div>
                    </div>
                </div>

                {/* Tips - rotating */}
                <div className="loading-tips">
                    <p className="tip">
                        {elapsedTime < 10 && (
                            <><strong>Tip:</strong> Our scanner runs a headless browser to capture exactly what users see.</>
                        )}
                        {elapsedTime >= 10 && elapsedTime < 20 && (
                            <><strong>Clinical Analysis:</strong> We capture evidence for every finding with confidence scores.</>
                        )}
                        {elapsedTime >= 20 && (
                            <><strong>Almost done:</strong> Your comprehensive diagnostic report is being compiled.</>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}

